
import { Language } from '../types';
import { translations } from '../utils/translations';
import React, { useState } from 'react';

interface AboutProps {
  language: Language;
}

type FormStatus = 'IDLE' | 'SUBMITTING' | 'SUCCESS' | 'ERROR';

export const About: React.FC<AboutProps> = ({ language }) => {
  const t = translations[language].about;
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<FormStatus>('IDLE');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Protección contra spam (Honeypot)
    if (honeypot) return;

    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    setStatus('SUBMITTING');

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('message', formData.message);
      data.append('_subject', `WEB_LEAD: ${formData.name}`);
      data.append('_captcha', 'false');
      data.append('_template', 'table');

      const response = await fetch('https://formsubmit.co/ajax/fabian@irreverentia.eu', {
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success === "true") {
        setStatus('SUCCESS');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('IDLE'), 5000);
      } else {
        console.error('Error de servidor FormSubmit:', result);
        setStatus('ERROR');
      }
    } catch (error) {
      console.error('Error de red/conectividad:', error);
      setStatus('ERROR');
    }
  };

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen flex items-center justify-center bg-black text-white scanlines">
      <div className="max-w-6xl w-full">

         {/* Executive Producers Contact Section */}
         <div className="w-full mb-24">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-b-4 border-zinc-800 pb-20">
                
                {/* SPAIN */}
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                   <div className="bg-[#ccff00] text-black px-4 py-1 mb-4 font-display font-black text-2xl border-r-4 border-b-4 border-[#b026ff]">
                     {t.epSpain}
                   </div>
                   <p className="font-display text-3xl text-white uppercase font-black mb-4">Fabián Aranda</p>
                   <div className="flex flex-col gap-2">
                       <a href="tel:+34633054460" className="font-mono text-zinc-400 hover:text-[#ccff00] transition-colors uppercase">
                         {t.contact.phone} +34 633 054 460
                       </a>
                       <a href="mailto:fabian@irreverentia.eu" className="font-mono text-zinc-400 hover:text-[#ccff00] transition-colors border-b-2 border-transparent hover:border-[#ccff00]">
                         fabian@irreverentia.eu
                       </a>
                   </div>
                </div>

                {/* MEXICO */}
                <div className="flex flex-col items-center md:items-end text-center md:text-right">
                   <div className="bg-[#ccff00] text-black px-4 py-1 mb-4 font-display font-black text-2xl border-l-4 border-b-4 border-[#b026ff]">
                     {t.epMexico}
                   </div>
                   <p className="font-display text-3xl text-white uppercase font-black mb-4">Jonathan Bouzali</p>
                   <div className="flex flex-col gap-2 items-center md:items-end">
                       <a href="tel:+525522716308" className="font-mono text-zinc-400 hover:text-[#ccff00] transition-colors uppercase">
                         {t.contact.phone} +52 5522716308
                       </a>
                       <a href="mailto:jonathan@kuter.tv" className="font-mono text-zinc-400 hover:text-[#ccff00] transition-colors border-b-2 border-transparent hover:border-[#ccff00]">
                         jonathan@kuter.tv
                       </a>
                   </div>
                </div>

             </div>
         </div>
         
         <div className="grid lg:grid-cols-2 gap-20 items-start">
            
            {/* Left Column: Manifesto */}
            <div>
              <div className="border-l-8 border-[#ccff00] pl-10 mb-12">
                  <h2 className="font-display text-6xl md:text-8xl text-white mb-4 uppercase leading-none tracking-tighter">
                    {t.title}
                  </h2>
                  <p className="font-mono text-[#ccff00] text-2xl tracking-widest uppercase font-bold">{t.subtitle}</p>
              </div>
              
              <div className="text-zinc-300 font-mono leading-relaxed space-y-8 text-xl">
                  <p className="border-b-2 border-zinc-900 pb-4">{t.intro1}</p>
                  <p className="border-b-2 border-zinc-900 pb-4">{t.intro2}</p>
                  
                  <ul className="space-y-6 pt-4">
                    {t.list.map((item, idx) => (
                      <li key={idx} className="flex items-center text-white font-black group">
                          <span className="w-4 h-4 bg-[#ccff00] mr-6 shrink-0 shadow-[4px_4px_0px_0px_#b026ff] group-hover:scale-125 transition-transform"></span>
                          {item}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-16 pt-10 border-t-4 border-zinc-900">
                     <p className="text-sm text-zinc-500 mb-6 uppercase tracking-[0.3em]">{t.contact.social}</p>
                     <div className="flex flex-wrap gap-8">
                        <a 
                          href="https://www.instagram.com/irr3verentia/" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-8bit !bg-zinc-900 !text-white hover:!bg-[#ccff00] hover:!text-black text-sm py-2 px-6"
                        >
                          INSTAGRAM
                        </a>
                        <a 
                          href="https://www.youtube.com/@irr3verentia" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-8bit !bg-zinc-900 !text-white hover:!bg-[#ccff00] hover:!text-black text-sm py-2 px-6"
                        >
                          YOUTUBE
                        </a>
                     </div>
                  </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-zinc-900 p-10 border-4 border-zinc-800 shadow-[15px_15px_0px_0px_#ccff00] relative">
               <div className="absolute -top-4 -left-4 w-8 h-8 bg-[#ccff00]"></div>
               <h3 className="font-display font-black text-4xl uppercase mb-10 text-white tracking-tighter">{t.contact.title}</h3>
               
               <form className="space-y-8" onSubmit={handleSubmit} noValidate>
                  <input type="text" name="_honey" value={honeypot} onChange={e => setHoneypot(e.target.value)} className="hidden" aria-hidden="true" tabIndex={-1} />

                  <div className="relative group">
                    <label className="block font-mono text-xs text-[#ccff00] mb-3 uppercase font-bold tracking-widest">{t.contact.name}</label>
                    <input 
                      type="text" 
                      name="name"
                      autoComplete="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-black border-2 border-zinc-700 p-5 text-white font-mono focus:border-[#ccff00] focus:outline-none transition-colors" 
                    />
                  </div>
                  <div className="relative group">
                    <label className="block font-mono text-xs text-[#ccff00] mb-3 uppercase font-bold tracking-widest">{t.contact.email}</label>
                    <input 
                      type="email" 
                      name="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-black border-2 border-zinc-700 p-5 text-white font-mono focus:border-[#ccff00] focus:outline-none transition-colors" 
                    />
                  </div>
                  <div className="relative group">
                    <label className="block font-mono text-xs text-[#ccff00] mb-3 uppercase font-bold tracking-widest">{t.contact.message}</label>
                    <textarea 
                      name="message"
                      rows={4} 
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-black border-2 border-zinc-700 p-5 text-white font-mono focus:border-[#ccff00] focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>
                  
                  <div className="relative">
                    <button 
                      type="submit" 
                      disabled={status === 'SUBMITTING'}
                      className={`btn-8bit w-full mt-6 text-2xl py-6 transition-all ${
                        status === 'SUCCESS' ? '!bg-green-500 !text-black' : 
                        status === 'ERROR' ? '!bg-red-600 !text-white' : ''
                      }`}
                    >
                       {status === 'SUBMITTING' ? 'TRANSMITIENDO...' : 
                        status === 'SUCCESS' ? '¡ENVIADO!' : 
                        status === 'ERROR' ? 'ERROR - REINTENTAR' : 
                        t.contact.send}
                    </button>
                    
                    {status === 'SUCCESS' && (
                      <p className="absolute -bottom-8 left-0 w-full text-center font-mono text-[10px] text-green-500 uppercase tracking-widest animate-pulse">
                        SISTEMA_OK. REVISA TU EMAIL PARA CONFIRMAR.
                      </p>
                    )}

                    {status === 'ERROR' && (
                      <p className="absolute -bottom-8 left-0 w-full text-center font-mono text-[10px] text-red-500 uppercase tracking-widest">
                        ERROR_PROTOCOLO. INTÉNTALO DE NUEVO.
                      </p>
                    )}
                  </div>
               </form>
            </div>

         </div>
      </div>
    </div>
  );
};
